import { useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ExternalLink, FolderOpen, Link2, Pencil, Trash2, Upload } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { copiarTexto } from '../../componentes/copiar';
import { Estado } from '../../componentes/estados';
import { Alternador, AreaSensivel, Rotulo } from '../../componentes/formulario';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, del, get, mensagemDeErro, patch } from '../../nucleo/api';
import { data, nomeDeArquivo, nomePessoa, numero, textoLivre } from '../../nucleo/formato';
import { CabecalhoConfig, CaixaConfig, CLASSE_TABELA_CONFIG, CLASSE_TD, CLASSE_TH } from './config-crm-base';
import { CAMINHO_CONFIGURACOES } from './crm-caminhos';
import { OutrosServicosConfig, PortfoliosCentral } from './PortfoliosCentral';

/**
 * Lote crm8 (item 3): Central de Arquivos, como em Configurações > Preferências > Central de Arquivos da origem (texto de
 * apresentação, "Upload" de vários arquivos e a tabela Data, Arquivo, Tamanho, Adicionado por, Abrir, Link, Compartilhado e
 * Opções). Quem não gere a Central (`pipeline.arquivos`) vê só os compartilhados, sem enviar nem mudar. O servidor confere o
 * tipo pelos bytes e o teto de novo.
 */

export interface ArquivoCentral {
  id: string;
  nome: string;
  mime: string;
  tamanho: number;
  compartilhado: boolean;
  /** Lista de 30/10 (B), item 20: o texto que entra no corpo do e-mail com o arquivo (antes da assinatura); null = sem. */
  texto_modelo?: string | null;
  /** Lista de 03/11: a vertical de que o arquivo é o portfólio, ou o portfólio geral do grupo. */
  portfolio_empresa_id?: string | null;
  portfolio_geral?: boolean;
  created_at: string;
  autor_nome: string | null;
}

export const CHAVE_CENTRAL = ['pipeline', 'central-arquivos'] as const;
/** Por arquivo (25 MiB, o teto do envio de arquivos do sistema; a soma de um e-mail é de 28 MiB, ver `portfolio-envio.ts`). */
export const LIMITE_CENTRAL = 25 * 1024 * 1024;
export const MAX_CENTRAL = 200;
const EXTENSOES = /\.(jpe?g|png|gif|webp|pdf|docx?|xlsx?|pptx?|odt|ods|txt|csv)$/i;
const ACEITOS = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.txt,.csv';

export const caminhoDoArquivoCentral = (id: string) => `/api/v1/pipeline/central-arquivos/${id}/arquivo`;
/** Tamanho em kB com duas casas, como na origem. */
export const kb = (b: number) => `${numero(b / 1024, 2)} kB`;

export function useCentral(habilitado = true) {
  return useQuery({
    queryKey: CHAVE_CENTRAL,
    queryFn: () => get<{ itens: ArquivoCentral[]; pode_gerir: boolean }>('/pipeline/central-arquivos'),
    enabled: habilitado,
    staleTime: 30_000,
  });
}

/** As abas das Preferências (a da Central e a do parabéns ligadas; as outras, "Em breve"). */
export function abasPreferencias(t: (k: string) => string) {
  return [
    { chave: 'troca', rotulo: t('crm.cfg.preferencias.abaTroca') },
    { chave: 'arquivos', rotulo: t('crm.cfg.preferencias.abaArquivos'), para: `${CAMINHO_CONFIGURACOES}/preferencias/arquivos` },
    { chave: 'gatilhos', rotulo: t('crm.cfg.preferencias.abaGatilhos') },
    { chave: 'negociacao', rotulo: t('crm.cfg.preferencias.abaNegociacao'), para: `${CAMINHO_CONFIGURACOES}/preferencias` },
    { chave: 'tokens', rotulo: t('crm.cfg.preferencias.abaTokens') },
  ];
}

/** Item 20: o campo do texto modelo (opcional, até 2000), no envio à Central e na edição. */
function TextoModelo({ id, valor, aoMudar }: { id: string; valor: string; aoMudar: (v: string) => void }) {
  const { t } = useTranslation('pipeline');
  return (
    <Rotulo id={id} rotulo={t('crm.cfg.central.textoModelo')} ajuda={t('crm.cfg.central.textoModeloAjuda', { n: valor.length })}>
      <AreaSensivel id={id} rows={3} maxLength={2000} placeholder={t('crm.cfg.central.textoModeloPlaceholder')} value={valor} onChange={(e) => aoMudar(e.target.value)} data-central-texto-modelo-campo="" />
    </Rotulo>
  );
}

export function CentralArquivosCrm() {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useCentral();
  const entrada = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState<string | null>(null);
  const [excluir, setExcluir] = useState<ArquivoCentral | null>(null);
  // Item 20: os arquivos escolhidos esperam o texto modelo (opcional) numa janela antes de subir; e a edição do texto depois.
  const [pendentes, setPendentes] = useState<File[] | null>(null);
  const [textoNovo, setTextoNovo] = useState('');
  const [editarTexto, setEditarTexto] = useState<{ arquivo: ArquivoCentral; texto: string } | null>(null);
  const limite = `${numero(LIMITE_CENTRAL / 1024 / 1024, 0)} MB`;
  const recarregar = () => qc.invalidateQueries({ queryKey: CHAVE_CENTRAL });
  const pode = !!q.data?.pode_gerir;

  const escolher = (lista: FileList | null) => {
    const arquivos = Array.from(lista ?? []);
    if (entrada.current) entrada.current.value = '';
    const ruim = arquivos.find((f) => !EXTENSOES.test(f.name));
    if (ruim) return toast.erro(t('crm.cfg.central.tipo', { nome: nomeDeArquivo(ruim.name) }));
    const grande = arquivos.find((f) => f.size > LIMITE_CENTRAL);
    if (grande) return toast.erro(t('crm.cfg.central.grande', { nome: nomeDeArquivo(grande.name), limite }));
    if (!arquivos.length) return;
    setTextoNovo('');
    setPendentes(arquivos);
  };
  const enviar = async (arquivos: File[], texto: string) => {
    setPendentes(null);
    let feitos = 0;
    try {
      for (const f of arquivos) {
        // Rodada rlote-2 (VIS-B1): o nome passa pelo `nomeDeArquivo` (modo discreto), como na tabela.
        setEnviando(nomeDeArquivo(f.name));
        const fd = new FormData();
        fd.append('id', crypto.randomUUID());
        if (texto.trim()) fd.append('texto_modelo', texto.trim());
        fd.append('arquivo', f);
        await api('POST', '/pipeline/central-arquivos', fd);
        feitos++;
      }
      if (feitos) toast.sucesso(t('crm.cfg.central.enviados', { count: feitos }));
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    } finally {
      setEnviando(null);
      await recarregar();
    }
  };
  const compartilhar = useMutation({
    mutationFn: (v: { id: string; compartilhado: boolean }) => patch(`/pipeline/central-arquivos/${v.id}`, { compartilhado: v.compartilhado }),
    onSuccess: async () => {
      toast.sucesso(t('crm.cfg.central.salvo'));
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const salvarTexto = useMutation({
    mutationFn: (v: { id: string; texto: string }) => patch(`/pipeline/central-arquivos/${v.id}`, { texto_modelo: v.texto.trim() || null }),
    onSuccess: async () => {
      toast.sucesso(t('crm.cfg.central.textoSalvo'));
      setEditarTexto(null);
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const remover = useMutation({
    mutationFn: (id: string) => del(`/pipeline/central-arquivos/${id}`),
    onSuccess: async () => {
      toast.sucesso(t('crm.cfg.central.excluido'));
      setExcluir(null);
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const copiar = async (a: ArquivoCentral) => {
    if (await copiarTexto(`${window.location.origin}${caminhoDoArquivoCentral(a.id)}`)) toast.sucesso(t('crm.cfg.central.linkCopiado'));
  };

  return (
    <section className="flex min-w-0 flex-col gap-5" data-config-central-arquivos="">
      <CabecalhoConfig
        titulo={t('crm.ficha2.config.secoes.preferencias')}
        abas={abasPreferencias(t)}
        ativa="arquivos"
        intro={
          <>
            <p>{t('crm.cfg.central.intro1')}</p>
            <p>{t('crm.cfg.central.intro2')}</p>
            <p>{t('crm.cfg.central.intro3', { limite, max: MAX_CENTRAL })}</p>
          </>
        }
      />
      {q.data && !pode && <p className="rounded-lg border border-borda bg-sup-1 px-3 py-2 text-sm text-tinta-2" data-central-so-leitura="">{t('crm.cfg.central.semPermissao')}</p>}
      {pode && (
        <div className="flex flex-wrap items-center gap-3">
          <input ref={entrada} type="file" multiple hidden accept={ACEITOS} onChange={(e) => escolher(e.target.files)} data-central-arquivo="" />
          <Botao variante="primario" icone={<Upload className="size-4" />} carregando={!!enviando} onClick={() => entrada.current?.click()} data-central-upload="">
            {t('crm.cfg.central.upload')}
          </Botao>
          {enviando && (
            <span role="status" className="min-w-0 truncate text-sm text-tinta-3" title={t('crm.cfg.central.enviando', { nome: enviando })}>
              {t('crm.cfg.central.enviando', { nome: enviando })}
            </span>
          )}
        </div>
      )}
      <Estado consulta={q}>
        {(d) => (
          <div className="min-w-0 rounded-xl border border-borda bg-sup-1">
            <div className={CLASSE_TABELA_CONFIG} role="region" tabIndex={0} aria-label={t('crm.cfg.preferencias.abaArquivos')}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-borda">
                    <th className={CLASSE_TH}>{t('crm.cfg.central.data')}</th>
                    <th className={CLASSE_TH}>{t('crm.cfg.central.arquivo')}</th>
                    <th className={CLASSE_TH}>{t('crm.cfg.central.tamanho')}</th>
                    <th className={CLASSE_TH}>{t('crm.cfg.central.adicionadoPor')}</th>
                    <th className={CLASSE_TH}>{t('crm.cfg.central.abrir')}</th>
                    <th className={CLASSE_TH}>{t('crm.cfg.central.link')}</th>
                    <th className={CLASSE_TH}>{t('crm.cfg.central.compartilhado')}</th>
                    {pode && <th className={`${CLASSE_TH} text-right`}>{t('crm.cfg.central.opcoes')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {d.itens.length === 0 && (
                    <tr>
                      <td colSpan={pode ? 8 : 7} className="px-3 py-6 text-center text-tinta-3">
                        {t('crm.cfg.central.vazio')}
                      </td>
                    </tr>
                  )}
                  {d.itens.map((a) => {
                    const nome = nomeDeArquivo(a.nome);
                    return (
                      <tr key={a.id} className="border-b border-borda last:border-b-0" data-arquivo-central={a.nome}>
                        <td className={`numero ${CLASSE_TD} whitespace-nowrap text-tinta-3`}>{data(a.created_at)}</td>
                        <td className={`${CLASSE_TD} max-w-72`}>
                          <span className="block truncate font-medium" title={nome}>
                            {nome}
                          </span>
                          {a.texto_modelo && (
                            <span className="block truncate text-xs text-tinta-3" title={t('crm.cfg.central.comTexto', { texto: textoLivre(a.texto_modelo) })} data-central-texto-modelo="">
                              {t('crm.cfg.central.comTexto', { texto: textoLivre(a.texto_modelo) })}
                            </span>
                          )}
                        </td>
                        <td className={`numero ${CLASSE_TD} whitespace-nowrap`}>{kb(a.tamanho)}</td>
                        <td className={`${CLASSE_TD} whitespace-nowrap`}>{nomePessoa(a.autor_nome ?? '')}</td>
                        <td className={CLASSE_TD}>
                          <a href={caminhoDoArquivoCentral(a.id)} target="_blank" rel="noopener" className="grid size-9 place-items-center rounded-lg text-acento-forte hover:bg-sup-3" aria-label={t('crm.cfg.central.abrirNome', { nome })} title={t('crm.cfg.central.abrirNome', { nome })}>
                            <ExternalLink className="size-4" aria-hidden />
                          </a>
                        </td>
                        <td className={CLASSE_TD}>
                          <Botao tamanho="sm" variante="fantasma" icone={<Link2 className="size-4" />} aria-label={t('crm.cfg.central.copiarLink', { nome })} title={t('crm.cfg.central.copiarLink', { nome })} onClick={() => void copiar(a)} data-central-link="" />
                        </td>
                        <td className={CLASSE_TD}>
                          <Alternador marcado={a.compartilhado} rotulo={t('crm.cfg.central.compartilhadoNome', { nome })} desabilitado={!pode || compartilhar.isPending} aoMudar={(v) => compartilhar.mutate({ id: a.id, compartilhado: v })} />
                        </td>
                        {pode && (
                          <td className={CLASSE_TD}>
                            <span className="flex justify-end">
                              <Botao tamanho="sm" variante="fantasma" icone={<Pencil className="size-4" />} aria-label={t('crm.cfg.central.editarTexto', { nome })} title={t('crm.cfg.central.editarTexto', { nome })} onClick={() => setEditarTexto({ arquivo: a, texto: a.texto_modelo ?? '' })} data-central-editar-texto="" />
                              <Botao tamanho="sm" variante="fantasma" icone={<Trash2 className="size-4" />} aria-label={t('crm.cfg.central.excluirNome', { nome })} title={t('crm.cfg.central.excluirNome', { nome })} onClick={() => setExcluir(a)} data-central-excluir="" />
                            </span>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Estado>
      <PortfoliosCentral />
      <OutrosServicosConfig />
      {pendentes && (
        <Modal
          aberto
          aoFechar={() => setPendentes(null)}
          titulo={t('crm.cfg.central.enviarTitulo')}
          rodape={
            <>
              <Botao variante="fantasma" onClick={() => setPendentes(null)}>
                {tn('comum.cancelar')}
              </Botao>
              <Botao variante="primario" icone={<Upload className="size-4" />} onClick={() => void enviar(pendentes, textoNovo)} data-central-enviar-confirmar="">
                {t('crm.cfg.central.enviarConfirmar')}
              </Botao>
            </>
          }
        >
          <div className="flex flex-col gap-3" data-central-enviar="">
            <ul className="flex flex-col divide-y divide-borda rounded-lg border border-borda text-sm">
              {pendentes.map((f, i) => (
                <li key={`${f.name}-${i}`} className="truncate px-3 py-2" title={nomeDeArquivo(f.name)}>
                  {nomeDeArquivo(f.name)}
                </li>
              ))}
            </ul>
            <TextoModelo id="central-texto-novo" valor={textoNovo} aoMudar={setTextoNovo} />
          </div>
        </Modal>
      )}
      {editarTexto && (
        <Modal
          aberto
          aoFechar={() => setEditarTexto(null)}
          titulo={t('crm.cfg.central.editarTexto', { nome: nomeDeArquivo(editarTexto.arquivo.nome) })}
          rodape={
            <>
              <Botao variante="fantasma" onClick={() => setEditarTexto(null)}>
                {tn('comum.cancelar')}
              </Botao>
              <Botao variante="primario" carregando={salvarTexto.isPending} onClick={() => salvarTexto.mutate({ id: editarTexto.arquivo.id, texto: editarTexto.texto })} data-central-salvar-texto="">
                {tn('comum.salvar')}
              </Botao>
            </>
          }
        >
          <TextoModelo id="central-texto-editar" valor={editarTexto.texto} aoMudar={(v) => setEditarTexto((x) => (x ? { ...x, texto: v } : x))} />
        </Modal>
      )}
      <Confirmar
        aberto={!!excluir}
        aoFechar={() => setExcluir(null)}
        perigoso
        titulo={t('crm.cfg.central.excluirTitulo')}
        texto={t('crm.cfg.central.excluirTexto', { nome: nomeDeArquivo(excluir?.nome ?? '') })}
        carregando={remover.isPending}
        aoConfirmar={() => excluir && remover.mutate(excluir.id)}
      />
    </section>
  );
}

/**
 * Lista de 30/10 (B), item 20: o texto do envio acompanha os arquivos da Central escolhidos. O texto modelo do arquivo que
 * entra vai ao fim do texto (se ainda não está lá); o do arquivo que sai é tirado (se segue igual). O que a pessoa escreveu
 * fica. O servidor põe o texto no corpo, antes da assinatura.
 */
export function textoDosArquivos(atual: string, antes: readonly ArquivoCentral[], depois: readonly ArquivoCentral[]): string {
  const ids = (l: readonly ArquivoCentral[]) => new Set(l.map((a) => a.id));
  const ficam = ids(depois);
  const eram = ids(antes);
  let texto = atual;
  for (const a of antes) {
    const m = a.texto_modelo?.trim();
    // rl0311-8 ROB8-B3: dois arquivos com o mesmo texto dividem a ocorrência: ela fica enquanto um deles fica.
    if (m && !ficam.has(a.id) && !depois.some((d) => d.texto_modelo?.trim() === m)) texto = texto.replace(m, '');
  }
  for (const a of depois) {
    const m = a.texto_modelo?.trim();
    if (m && !eram.has(a.id) && !texto.includes(m)) texto = `${texto.trim()}\n\n${m}`;
  }
  return texto.replace(/\n{3,}/g, '\n\n').trim().slice(0, 2000);
}

/**
 * "Arquivos da Central" no "Criar e-mail" e no envio da proposta: escolher vários dos que a pessoa vê (o servidor confere de
 * novo). Devolve os escolhidos; quem chama confere o teto junto com os outros anexos.
 */
export function EscolherArquivosCentral({ inicial, aoFechar, aoEscolher }: { inicial: ArquivoCentral[]; aoFechar: () => void; aoEscolher: (a: ArquivoCentral[]) => void }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const q = useCentral();
  const id = useId();
  const [marcados, setMarcados] = useState<string[]>(() => inicial.map((a) => a.id));
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('crm.cfg.central.escolherTitulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" disabled={!q.data} onClick={() => aoEscolher((q.data?.itens ?? []).filter((a) => marcados.includes(a.id)))} data-central-usar="">
            {t('crm.cfg.central.escolherConfirmar')}
          </Botao>
        </>
      }
    >
      <p id={`${id}-ajuda`} className="mb-3 text-sm text-tinta-2">
        {t('crm.cfg.central.escolherAjuda')}
      </p>
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <p className="flex items-center gap-2 py-4 text-sm text-tinta-3">
              <FolderOpen className="size-4" aria-hidden />
              {t('crm.cfg.central.escolherVazio')}
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-borda rounded-lg border border-borda" aria-describedby={`${id}-ajuda`} data-central-lista="">
              {d.itens.map((a) => {
                const nome = nomeDeArquivo(a.nome);
                return (
                  <li key={a.id} className="flex min-h-11 items-center gap-3 px-3 py-1.5 text-sm" data-central-opcao={a.nome}>
                    <CaixaConfig marcado={marcados.includes(a.id)} rotulo={t('crm.cfg.central.escolherNome', { nome })} aoMudar={(v) => setMarcados((m) => (v ? [...m, a.id] : m.filter((x) => x !== a.id)))} />
                    <span className="min-w-0 flex-1 truncate" title={nome}>
                      {nome}
                    </span>
                    <span className="numero shrink-0 text-xs text-tinta-3">{kb(a.tamanho)}</span>
                  </li>
                );
              })}
            </ul>
          )
        }
      </Estado>
    </Modal>
  );
}
